import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FolderKanban, Search, Calendar, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'
import type { Project, PaginationMeta } from '../types'
import { formatDate, formatCurrency } from '../utils/helpers'
import Pagination from '../components/Pagination'
import { useModules } from '../hooks/useModules'

export default function Projects() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { enabledModules, disableModule } = useModules()
  const [projects, setProjects] = useState<Project[]>([])
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortField, setSortField] = useState<string | null>('date_start')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  const loadProjects = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.getProjects({ page, limit })
      const list = Array.isArray(response) ? response : (response?.data ?? [])
      const pag = Array.isArray(response) ? null : (response?.pagination ?? null)
      setProjects(list)
      setFilteredProjects(list)
      setPagination(pag)
      setError(null)
    } catch (err: unknown) {
      const apiErr = err as { response?: { status?: number } }
      if (apiErr?.response?.status === 403) {
        disableModule('projects')
        navigate('/dashboard', { replace: true })
        return
      }
      console.error('Erreur chargement projets:', err)
      setError(getApiErrorKey(err, 'projects.loadError'))
      setProjects([])
      setFilteredProjects([])
    } finally {
      setLoading(false)
    }
  }, [page, limit, disableModule, navigate])

  const filterProjects = useCallback(() => {
    let filtered = projects

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.ref.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => String(p.status) === statusFilter)
    }

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const va = (a as unknown as Record<string, unknown>)[sortField] ?? ''
        const vb = (b as unknown as Record<string, unknown>)[sortField] ?? ''
        if (typeof va === 'number' && typeof vb === 'number')
          return sortDir === 'asc' ? va - vb : vb - va
        return sortDir === 'asc'
          ? String(va).localeCompare(String(vb))
          : String(vb).localeCompare(String(va))
      })
    }

    setFilteredProjects(filtered)
  }, [projects, searchTerm, statusFilter, sortField, sortDir])

  useEffect(() => {
    if (enabledModules.projects === false) {
      navigate('/dashboard', { replace: true })
      return
    }
    loadProjects()
  }, [page, limit, enabledModules.projects, loadProjects, navigate])
  useEffect(() => {
    filterProjects()
  }, [filterProjects])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300 inline ml-1" />
    return sortDir === 'asc' ? (
      <ChevronUp className="w-3 h-3 text-primary-600 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 text-primary-600 inline ml-1" />
    )
  }

  const getStatusBadge = (status: number, label?: string) => {
    const styles: Record<number, string> = {
      0: 'bg-gray-100 text-gray-800',
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800',
    }
    return (
      <span
        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${styles[status] ?? 'bg-gray-100 text-gray-800'}`}
      >
        {label ?? t('projects.statusUnknown')}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-gray-900">{t('projects.title')}</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 mb-4">{t(error)}</p>
          <button
            onClick={loadProjects}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <p className="text-sm font-medium text-gray-600">{t('projects.totalProjects')}</p>
        <p className="text-3xl font-bold text-gray-900 mt-2">{filteredProjects.length}</p>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('projects.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
          >
            <option value="all">{t('common.allStatuses')}</option>
            <option value="0">{t('projects.statusDraft')}</option>
            <option value="1">{t('projects.statusValidated')}</option>
            <option value="2">{t('projects.statusClosed')}</option>
          </select>
        </div>
      </div>

      {/* Liste des projets */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {filteredProjects.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <FolderKanban className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">{t('projects.notFound')}</p>
          </div>
        ) : (
          <>
            {/* Mobile card layout */}
            <ul className="divide-y divide-gray-100 sm:hidden">
              {filteredProjects.map((project) => (
                <li key={project.id} className="p-4 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {pagination && (
                        <p className="text-xs text-gray-500 truncate">
                          {project.customer_name ?? '—'}
                        </p>
                      )}
                      <p className="font-medium text-gray-900 text-sm">{project.title}</p>
                      <p className="text-xs text-primary-600 font-mono">{project.ref}</p>
                    </div>
                    {getStatusBadge(project.status, project.status_label)}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{project.date_start ? formatDate(project.date_start) : '-'}</span>
                      {project.date_end && <span>→ {formatDate(project.date_end)}</span>}
                    </div>
                    {project.budget && (
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(project.budget)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {pagination && (
                      <th
                        className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('customer_name')}
                      >
                        {t('common.client')} <SortIcon field="customer_name" />
                      </th>
                    )}
                    <th
                      className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('ref')}
                    >
                      {t('projects.colRef')} <SortIcon field="ref" />
                    </th>
                    <th
                      className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('title')}
                    >
                      {t('projects.colTitle')} <SortIcon field="title" />
                    </th>
                    <th
                      className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('date_start')}
                    >
                      {t('projects.colDateStart')} <SortIcon field="date_start" />
                    </th>
                    <th
                      className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('date_end')}
                    >
                      {t('projects.colDateEnd')} <SortIcon field="date_end" />
                    </th>
                    <th
                      className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('budget')}
                    >
                      {t('projects.colBudget')} <SortIcon field="budget" />
                    </th>
                    <th
                      className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      {t('projects.colStatus')} <SortIcon field="status" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProjects.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                      {pagination && (
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium whitespace-nowrap">
                          {project.customer_name ?? '—'}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-mono text-primary-600 font-medium">
                        {project.ref}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{project.title}</p>
                          {project.description && (
                            <p className="text-sm text-gray-500 line-clamp-1 mt-1">
                              {project.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {project.date_start ? formatDate(project.date_start) : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {project.date_end ? formatDate(project.date_end) : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        {project.budget ? (
                          <div className="flex items-center justify-end gap-1 font-medium text-gray-900">
                            {formatCurrency(project.budget)}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(project.status, project.status_label)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {pagination && (
          <div className="px-6 py-4">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
